// packages/web/src/App.tsx
import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PlatformAdminRoute } from './components/auth/PlatformAdminRoute';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import Layout from './components/Layout';

// ── Lazy pages ────────────────────────────────────────────────
const LoginPage           = lazy(() => import('./pages/LoginPage'));
const DashboardPage       = lazy(() => import('./pages/DashboardPage'));
const ForgotPasswordPage  = lazy(() => import('./pages/ForgotPasswordPage'));
const AcceptInvitePage    = lazy(() => import('./pages/AcceptInvitePage'));
const NotFoundPage        = lazy(() => import('./pages/NotFoundPage'));
const EmployeesPage       = lazy(() => import('./pages/EmployeesPage'));
const AttendancePage      = lazy(() => import('./pages/AttendancePage'));
const LeavePage           = lazy(() => import('./pages/LeavePage'));
const PayrollPage         = lazy(() => import('./pages/PayrollPage'));
const PayrollRunPage      = lazy(() => import('./pages/PayrollRunPage'));
const MyPayslipsPage      = lazy(() => import('./pages/MyPayslipsPage'));
const TdsDeclarationPage  = lazy(() => import('./pages/TdsDeclarationPage'));
const MastersPage         = lazy(() => import('./pages/MastersPage'));
const EmployeeProfilePage = lazy(() => import('./pages/EmployeeProfilePage'));
const LeaveManagementPage = lazy(() => import('./pages/LeaveManagementPage'));
const TaskManagementPage  = lazy(() => import('./pages/TaskManagementPage'));
const OperationsPage      = lazy(() => import('./pages/OperationsPage'));
const ReportsPage         = lazy(() => import('./pages/ReportsPage'));
const OnboardingOffboardingPage = lazy(() => import('./pages/OnboardingOffboardingPage'));
const AuditLogsPage       = lazy(() => import('./pages/AuditLogsPage'));
const WorkforceManagementPage = lazy(() => import('./pages/WorkforceManagementPage'));
const SalaryCompliancePage = lazy(() => import('./pages/SalaryCompliancePage'));
const EmployeeLifecyclePage = lazy(() => import('./pages/EmployeeLifecyclePage'));
const OrganisationDetailsPage = lazy(() => import('./pages/OrganisationDetailsPage'));
const TimeTrackerPage     = lazy(() => import('./pages/TimeTrackerPage'));
const OrganizationPage    = lazy(() => import('./pages/OrganizationPage'));

// ── SaaS Platform Pages (Pillars 1-3) ────────────────────────
const OrganizationSignUpPage     = lazy(() => import('./pages/OrganizationSignUpPage'));
const EmployeeReferenceSignupPage = lazy(() => import('./pages/EmployeeReferenceSignupPage'));
const OrgSettingsPage            = lazy(() => import('./pages/OrgSettingsPage'));
const UserManagementPage         = lazy(() => import('./pages/UserManagementPage'));

// ── greytHR Feature Parity Pages ──────────────────────────────
const Form16Page              = lazy(() => import('./pages/Form16Page'));
const AttendanceMusterPage    = lazy(() => import('./pages/AttendanceMusterPage'));
const LeaveOverviewPage       = lazy(() => import('./pages/LeaveOverviewPage'));
const HolidayListPage         = lazy(() => import('./pages/HolidayListPage'));
const ClaimsBatchPage         = lazy(() => import('./pages/ClaimsBatchPage'));
const PerformancePage         = lazy(() => import('./pages/PerformancePage'));
const WorkflowPage            = lazy(() => import('./pages/WorkflowPage'));
const ReportsGalleryPage      = lazy(() => import('./pages/ReportsGalleryPage'));
const EngagePage              = lazy(() => import('./pages/EngagePage'));
const HelpdeskPage            = lazy(() => import('./pages/HelpdeskPage'));

// ── Master Blueprint Pages ────────────────────────────────────
const SaaSAdminPage           = lazy(() => import('./pages/SaaSAdminPage'));
const SubscriptionPage        = lazy(() => import('./pages/SubscriptionPage'));
const LoansPage               = lazy(() => import('./pages/LoansPage'));
const SettlementPage          = lazy(() => import('./pages/SettlementPage'));
const TrainingPage            = lazy(() => import('./pages/TrainingPage'));
const ServiceRequestsPage     = lazy(() => import('./pages/ServiceRequestsPage'));
const AssetsPage              = lazy(() => import('./pages/AssetsPage'));
const CompanyPoliciesPage     = lazy(() => import('./pages/CompanyPoliciesPage'));
const LetterTemplatesPage     = lazy(() => import('./pages/LetterTemplatesPage'));
const EmployeeDirectoryPage   = lazy(() => import('./pages/EmployeeDirectoryPage'));
const EntityManagementPage    = lazy(() => import('./pages/EntityManagementPage'));
const PayrollManagementPage   = lazy(() => import('./pages/PayrollManagementPage'));
const ReportsAnalyticsPage    = lazy(() => import('./pages/ReportsAnalyticsPage'));
const GoldenRecordPage        = lazy(() => import('./pages/GoldenRecordPage'));

// ── Unified HR & Payroll (Phase 10) ──────────────────────────
const StatutorySettingsPage   = lazy(() => import('./pages/StatutorySettingsPage'));
const ITDeclarationPage       = lazy(() => import('./pages/ITDeclarationPage'));
const LMSPage                 = lazy(() => import('./pages/LMSPage'));
const SurveysPage             = lazy(() => import('./pages/SurveysPage'));
const FBPPage                 = lazy(() => import('./pages/FBPPage'));
const ReimbursementsPage      = lazy(() => import('./pages/ReimbursementsPage'));
const SalaryRevisionPage      = lazy(() => import('./pages/SalaryRevisionPage'));
const NoticeBoardPage         = lazy(() => import('./pages/NoticeBoardPage'));
const KnowledgeBasePage       = lazy(() => import('./pages/KnowledgeBasePage'));
const MyTeamPage              = lazy(() => import('./pages/MyTeamPage'));
const MyProfilePage           = lazy(() => import('./pages/MyProfilePage'));
const ResetPasswordPage       = lazy(() => import('./pages/ResetPasswordPage'));

// ── Developer Console (removed) ───────────────────────────────
const PendingApprovalsPage    = lazy(() => import('./pages/PendingApprovalsPage'));

// ── Phase 15: Advanced CTC, Manager, Policy, WFH, Skills, Compliance ──
const CTCConfiguratorPage     = lazy(() => import('./pages/CTCConfiguratorPage'));
const ManagerPortalPage       = lazy(() => import('./pages/ManagerPortalPage'));
const WFHManagementPage       = lazy(() => import('./pages/WFHManagementPage'));
const SkillsGoalsPage         = lazy(() => import('./pages/SkillsGoalsPage'));
const IndiaCompliancePage     = lazy(() => import('./pages/IndiaCompliancePage'));
const NotificationCenterPage  = lazy(() => import('./pages/NotificationCenterPage'));
const DynamicPolicyPage       = lazy(() => import('./pages/DynamicPolicyPage'));
const GlobalHelpdeskPage      = lazy(() => import('./pages/GlobalHelpdeskPage'));

// ── Enterprise RBAC & Billing (Phase 16) ──────────────────────
const RolesPermissionsPage    = lazy(() => import('./pages/RolesPermissionsPage'));
const BillingPortalPage       = lazy(() => import('./pages/BillingPortalPage'));

// ── Employee Lifecycle (Phase 13) ─────────────────────────────
const LifecycleFlowchartPage  = lazy(() => import('./pages/LifecycleFlowchartPage'));
const ManpowerRequisitionPage = lazy(() => import('./pages/ManpowerRequisitionPage'));
const InterviewAssessmentPage = lazy(() => import('./pages/InterviewAssessmentPage'));
const OvertimeManagementPage  = lazy(() => import('./pages/OvertimeManagementPage'));
const CompOffPage             = lazy(() => import('./pages/CompOffPage'));
const ExitManagementPage      = lazy(() => import('./pages/ExitManagementPage'));
const AlumniPage              = lazy(() => import('./pages/AlumniPage'));

// ── Phase 14: System Restructure ──────────────────────────────
const OrgHierarchyPage        = lazy(() => import('./pages/OrgHierarchyPage'));
const ApprovalMatrixPage      = lazy(() => import('./pages/ApprovalMatrixPage'));
const AccessConfigPage        = lazy(() => import('./pages/AccessConfigPage'));

// ── Missing Lifecycle Step Pages ──────────────────────────────
const JobPostingPage            = lazy(() => import('./pages/JobPostingPage'));
const ResumeScreeningPage       = lazy(() => import('./pages/ResumeScreeningPage'));
const InterviewSchedulingPage   = lazy(() => import('./pages/InterviewSchedulingPage'));
const BackgroundVerificationPage = lazy(() => import('./pages/BackgroundVerificationPage'));
const SalaryNegotiationPage     = lazy(() => import('./pages/SalaryNegotiationPage'));
const OfferAcceptancePage       = lazy(() => import('./pages/OfferAcceptancePage'));
const PreJoiningDocsPage        = lazy(() => import('./pages/PreJoiningDocsPage'));
const MedicalExaminationPage    = lazy(() => import('./pages/MedicalExaminationPage'));
const BonusIncentivesPage       = lazy(() => import('./pages/BonusIncentivesPage'));
const ExitInterviewPage         = lazy(() => import('./pages/ExitInterviewPage'));

// ── Lifecycle Engine (Dynamic Step Viewer & Permissions) ──────
const LifecycleStepViewerPage   = lazy(() => import('./pages/LifecycleStepViewerPage'));
const LifecyclePermissionsPage  = lazy(() => import('./pages/LifecyclePermissionsPage'));

// ── Loader ────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
      <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.12)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Guards ────────────────────────────────────────────────────
const ALUMNI_ALLOWED_PATHS = ['/my/payslips', '/form16', '/my/profile', '/dashboard', '/letter-templates'];

function PrivateRoute() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  if (isLoading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Alumni restriction: redirect to dashboard if accessing a page outside allowed set
  const roleName = user?.role?.name?.toLowerCase() ?? '';
  if (roleName === 'alumni' && !ALUMNI_ALLOWED_PATHS.some(p => location.pathname.startsWith(p))) {
    return <Navigate to="/dashboard" replace />;
  }

  // Wrap all private pages in the sidebar Layout
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function PublicRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <PageLoader />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

// ── App ───────────────────────────────────────────────────────
export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>

            <Route path="/" element={<Navigate to="/login" replace />} />

            {/* Public */}
            <Route element={<PublicRoute />}>
              <Route path="/login"              element={<LoginPage />} />
              <Route path="/forgot-password"    element={<ForgotPasswordPage />} />
              <Route path="/reset-password"     element={<ResetPasswordPage />} />
              <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/signup"              element={<OrganizationSignUpPage />} />
              <Route path="/join"                element={<EmployeeReferenceSignupPage />} />
            </Route>

            {/* Private — all wrapped in Layout sidebar */}
            <Route element={<PrivateRoute />}>
              <Route path="/dashboard"            element={<DashboardPage />} />
              <Route path="/employees"            element={<EmployeesPage />} />
              <Route path="/employees/:employeeId/profile" element={<EmployeeProfilePage />} />
              <Route path="/masters"              element={<MastersPage />} />
              <Route path="/attendance"           element={<AttendancePage />} />
              <Route path="/leave"                element={<LeavePage />} />
              <Route path="/leave-management"     element={<LeaveManagementPage />} />
              <Route path="/tasks"                element={<TaskManagementPage />} />
              <Route path="/operations"           element={<OperationsPage />} />
              <Route path="/reports"              element={<ReportsPage />} />
              <Route path="/payroll"              element={<PayrollPage />} />
              <Route path="/payroll/runs/:runId"  element={<PayrollRunPage />} />
              <Route path="/my/payslips"          element={<MyPayslipsPage />} />
              <Route path="/my/tds-declaration"   element={<TdsDeclarationPage />} />
              <Route path="/onboarding-offboarding" element={<OnboardingOffboardingPage />} />
              <Route path="/audit-logs"           element={<AuditLogsPage />} />
              <Route path="/workforce-management" element={<WorkforceManagementPage />} />
              <Route path="/salary-compliance"    element={<SalaryCompliancePage />} />
              <Route path="/employee-lifecycle"   element={<EmployeeLifecyclePage />} />
              <Route path="/organisation-details" element={<Navigate to="/org-settings" replace />} />
              <Route path="/time-tracker"         element={<TimeTrackerPage />} />
              <Route path="/organization"         element={<OrganizationPage />} />
              <Route path="/org-settings"         element={<OrgSettingsPage />} />
              <Route path="/user-management"      element={<UserManagementPage />} />
              {/* greytHR Feature Parity */}
              <Route path="/form16"               element={<Form16Page />} />
              <Route path="/attendance-muster"     element={<AttendanceMusterPage />} />
              <Route path="/leave-overview"        element={<LeaveOverviewPage />} />
              <Route path="/holidays"              element={<HolidayListPage />} />
              <Route path="/claims"                element={<ClaimsBatchPage />} />
              <Route path="/performance"           element={<PerformancePage />} />
              <Route path="/workflows"             element={<WorkflowPage />} />
              <Route path="/reports-gallery"       element={<ReportsGalleryPage />} />
              <Route path="/engage"                element={<EngagePage />} />
              <Route path="/helpdesk"              element={<HelpdeskPage />} />
              {/* Master Blueprint Pages */}
              <Route path="/saas-admin"            element={<PlatformAdminRoute><SaaSAdminPage /></PlatformAdminRoute>} />
              <Route path="/subscription"          element={<PlatformAdminRoute><SubscriptionPage /></PlatformAdminRoute>} />
              <Route path="/loans"                 element={<LoansPage />} />
              <Route path="/settlement"            element={<SettlementPage />} />
              <Route path="/training"              element={<TrainingPage />} />
              <Route path="/service-requests"      element={<ServiceRequestsPage />} />
              <Route path="/assets"                element={<AssetsPage />} />
              <Route path="/company-policies"      element={<CompanyPoliciesPage />} />
              <Route path="/letter-templates"      element={<LetterTemplatesPage />} />
              <Route path="/employee-directory"    element={<EmployeeDirectoryPage />} />
              <Route path="/entity-management"     element={<EntityManagementPage />} />
              <Route path="/payroll-management"    element={<PayrollManagementPage />} />
              <Route path="/reports-analytics"     element={<ReportsAnalyticsPage />} />
              <Route path="/golden-record"         element={<GoldenRecordPage />} />
              {/* Unified HR & Payroll (Phase 10) */}
              <Route path="/statutory"             element={<StatutorySettingsPage />} />
              <Route path="/it-declarations"       element={<ITDeclarationPage />} />
              <Route path="/lms"                   element={<LMSPage />} />
              <Route path="/surveys"               element={<SurveysPage />} />
              <Route path="/fbp"                   element={<FBPPage />} />
              <Route path="/reimbursements"        element={<ReimbursementsPage />} />
              <Route path="/salary-revision"       element={<SalaryRevisionPage />} />
              <Route path="/notice-board"          element={<NoticeBoardPage />} />
              <Route path="/knowledge-base"        element={<KnowledgeBasePage />} />
              <Route path="/my/team"               element={<MyTeamPage />} />
              <Route path="/my/profile"             element={<MyProfilePage />} />
              {/* Employee Lifecycle (Phase 13) */}
              <Route path="/lifecycle-flowchart"     element={<LifecycleFlowchartPage />} />
              <Route path="/manpower-requisition"    element={<ManpowerRequisitionPage />} />
              <Route path="/interview-assessment"    element={<InterviewAssessmentPage />} />
              <Route path="/overtime-management"     element={<OvertimeManagementPage />} />
              <Route path="/comp-off"                element={<CompOffPage />} />
              <Route path="/exit-management"         element={<ExitManagementPage />} />
              <Route path="/alumni"                  element={<AlumniPage />} />
              {/* Phase 14: Hierarchy, Approvals, Access */}
              <Route path="/org-hierarchy"           element={<OrgHierarchyPage />} />
              <Route path="/approval-matrix"         element={<ApprovalMatrixPage />} />
              <Route path="/access-config"           element={<AccessConfigPage />} />
              {/* Missing Lifecycle Step Pages */}
              <Route path="/job-postings"            element={<JobPostingPage />} />
              <Route path="/resume-screening"        element={<ResumeScreeningPage />} />
              <Route path="/interview-scheduling"    element={<InterviewSchedulingPage />} />
              <Route path="/background-verification" element={<BackgroundVerificationPage />} />
              <Route path="/salary-negotiation"      element={<SalaryNegotiationPage />} />
              <Route path="/offer-acceptance"        element={<OfferAcceptancePage />} />
              <Route path="/pre-joining-docs"        element={<PreJoiningDocsPage />} />
              <Route path="/medical-examination"     element={<MedicalExaminationPage />} />
              <Route path="/bonus-incentives"        element={<BonusIncentivesPage />} />
              <Route path="/exit-interview"          element={<ExitInterviewPage />} />
              {/* Lifecycle Engine (Dynamic Step Viewer & Permissions) */}
              <Route path="/lifecycle-step-viewer"   element={<LifecycleStepViewerPage />} />
              <Route path="/lifecycle-permissions"   element={<LifecyclePermissionsPage />} />
              {/* Developer Console (removed) */}
              <Route path="/pending-approvals"     element={<PendingApprovalsPage />} />
              {/* Phase 15: Advanced Modules */}
              <Route path="/ctc-configurator"      element={<CTCConfiguratorPage />} />
              <Route path="/manager-portal"        element={<ManagerPortalPage />} />
              <Route path="/wfh-management"        element={<WFHManagementPage />} />
              <Route path="/skills-goals"          element={<SkillsGoalsPage />} />
              <Route path="/india-compliance"      element={<IndiaCompliancePage />} />
              <Route path="/notifications"         element={<NotificationCenterPage />} />
              <Route path="/dynamic-policies"      element={<DynamicPolicyPage />} />
              <Route path="/errors-reported"       element={<PlatformAdminRoute><GlobalHelpdeskPage /></PlatformAdminRoute>} />
              {/* Enterprise RBAC & Billing (Phase 16) */}
              <Route path="/roles-permissions"     element={<RolesPermissionsPage />} />
              <Route path="/billing-portal"        element={<ProtectedRoute requiredPermission="canManageOrg"><BillingPortalPage /></ProtectedRoute>} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />

          </Routes>
        </Suspense>
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}